import React, { useState, useContext } from 'react';
import { useClerk } from '@clerk/clerk-react';
import { useAction } from 'convex/react';
import { Dialog, DialogBody, DialogTitle, DialogActions } from '../../catalyst-components/dialog';
import { Button } from '../../catalyst-components/button';
import { Input } from '../../catalyst-components/input';
import { Field, Label } from '../../catalyst-components/fieldset';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import { BaseContext } from '../base-context';

interface DeleteAccountDialogProps {
  open: boolean;
  onClose: () => void;
}

export const DeleteAccountDialog: React.FC<DeleteAccountDialogProps> = ({ open, onClose }) => {
  const { convexUserId } = useContext(BaseContext);
  const { signOut } = useClerk();
  const deleteAccount = useAction(api.users.deleteAccount);

  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isConfirmed = confirmText === 'DELETE';

  const handleClose = () => {
    setConfirmText('');
    setError(null);
    onClose();
  };

  const handleDelete = async () => {
    if (!isConfirmed || !convexUserId) return;

    setIsDeleting(true);
    setError(null);

    try {
      await deleteAccount({ user_id: convexUserId as Id<"users"> });
      signOut({ redirectUrl: window.location.origin });
    } catch (err) {
      setError('Failed to delete account. Please try again.');
      setIsDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} size="md">
      <DialogBody>
        <DialogTitle>Delete Account</DialogTitle>
        <p className="mt-2 text-sm text-muted-foreground">
          This action is permanent and cannot be undone. All your data, progress, sessions, and history will be permanently deleted.
        </p>

        <div className="mt-4">
          <Field>
            <Label>Type DELETE to confirm</Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="DELETE"
              disabled={isDeleting}
            />
          </Field>
        </div>

        {error && (
          <p className="mt-2 text-sm text-red-500">{error}</p>
        )}

        <DialogActions>
          <Button color="light" onClick={handleClose} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDelete}
            disabled={!isConfirmed || isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete My Account'}
          </Button>
        </DialogActions>
      </DialogBody>
    </Dialog>
  );
};
